import { getLocalToday } from "utils/Common/Date";
import React, { useState } from "react";
import PhoneInput from "../../../../Components/Common/PhoneInput/PhoneInput";
import { EMAIL_REGEX } from "../../../../utils/Common/Constant";
import Button from "../../../../Components/Common/Button/Button";
import SectionCard from "./SectionCard";
import DateSelect from "../../../../Components/Common/DateSelect/DateSelect";
import ErrorMessage from "../../../../Components/Common/ErrorMessage/ErrorMessage";
import NextIcon from "Assets/Img/NextIcon";
import PrevIcon from "Assets/Img/PrevIcon";
import Skeleton from "../../../../Components/Common/Skeleton/Skeleton";
import Input from "../../../../Components/Common/Input/Input";

interface DrawDetails {
  bank: string;
  drawNo: string;
  date: string;
  location: string;
  borrower: string;
  contractor: string;
  phone: string;
  email: string;
  periodTo: string;
  payee: string;
  mailTo: string;
}

interface DrawDetailsStepProps {
  drawDetails: DrawDetails;
  projectName: string;
  onChange: (key: keyof DrawDetails, value: string) => void;
  onNext: () => void;
  onOpenModal?: () => void;
  lienSubs?: unknown[];
  handleDelete?: (id: number) => void;
  onBack: () => void;
  loading?: boolean;
}

const DrawDetailsStep = ({
  drawDetails,
  projectName,
  onChange,
  onNext,
  onBack,
  loading = false,
}: DrawDetailsStepProps) => {
  const [errors, setErrors] = useState<
    Partial<Record<keyof DrawDetails, string>>
  >({});
  const handleNext = () => {
    const newErrors: Partial<Record<keyof DrawDetails, string>> = {};
    if (!drawDetails.periodTo) {
      newErrors.periodTo = "Period To is required";
    } else if (drawDetails.date && drawDetails.periodTo > drawDetails.date) {
      newErrors.periodTo = "Period To cannot be after the draw date";
    }
    if (!drawDetails.payee)
      newErrors.payee = "Make check payable to is required";
    if (!drawDetails.mailTo) {
      newErrors.mailTo = "Mail check to is required";
    } else if (!EMAIL_REGEX.test(drawDetails.mailTo)) {
      newErrors.mailTo = "Enter a valid email address";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstKey = Object.keys(newErrors)[0] as keyof DrawDetails;
      const idMap: Partial<Record<keyof DrawDetails, string>> = {
        periodTo: "draw-period",
        payee: "draw-payee",
        mailTo: "draw-mailto",
      };
      const el = document.getElementById(idMap[firstKey] ?? "");
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.focus();
      return;
    }
    setErrors({});
    onNext();
  };

  if (loading) {
    return (
      <section className="screen activeScreen">
        <Skeleton width="40%" height={22} style={{ marginBottom: 8 }} />
        <Skeleton width="60%" height={14} style={{ marginBottom: 24 }} />
        {[3, 2, 2].map((cols, i) => (
          <div
            key={i}
            className={`fieldRow cols${cols}`}
            style={{ marginBottom: 20 }}
          >
            {Array.from({ length: cols }).map((_, j) => (
              <div key={j} className="field">
                <Skeleton width="50%" height={11} style={{ marginBottom: 6 }} />
                <Skeleton width="100%" height={38} borderRadius={7} />
              </div>
            ))}
          </div>
        ))}
      </section>
    );
  }

  return (
    <section
      className="screen activeScreen"
      aria-labelledby="draw-details-title"
    >
      <h2 className="sectionTitle" id="draw-details-title">
        Draw Request Details
      </h2>
      <p className="sectionSub">
        Construction Disbursement Request - {projectName}
      </p>

      <SectionCard title="Project & Draw Info">
        <div className="fieldRow cols3">
          <div className="field">
            <label htmlFor="draw-bank">
              Bank <span className="req">*</span>
            </label>
            <Input
              id="draw-bank"
              className=""
              value={drawDetails.bank}
              disabled
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChange("bank", e.target.value)
              }
            />
          </div>
          <div className="field">
            <label htmlFor="draw-number">
              Draw No. <span className="req">*</span>
            </label>
            <Input
              id="draw-number"
              className=""
              value={drawDetails.drawNo}
              disabled
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChange("drawNo", e.target.value)
              }
            />
          </div>
          <div className="field">
            <label htmlFor="draw-date">
              Date <span className="req">*</span>
            </label>
            <DateSelect
              id="draw-date"
              value={drawDetails.date}
              disabled
              onChange={(val) => onChange("date", val)}
            />
          </div>
        </div>

        <div className="fieldRow cols2">
          <div className="field">
            <label htmlFor="draw-location">
              Location / Project Address <span className="req">*</span>
            </label>
            <Input
              id="draw-location"
              className=""
              value={drawDetails.location}
              disabled
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChange("location", e.target.value)
              }
            />
          </div>
          <div className="field">
            <label htmlFor="draw-borrower">
              Borrower <span className="req">*</span>
            </label>
            <Input
              id="draw-borrower"
              className=""
              value={drawDetails.borrower}
              disabled
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChange("borrower", e.target.value)
              }
            />
          </div>
        </div>

        <div className="fieldRow cols2">
          <div className="field">
            <label htmlFor="draw-contractor">
              Contractor <span className="req">*</span>
            </label>
            <Input
              id="draw-contractor"
              className=""
              value={drawDetails.contractor}
              disabled
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChange("contractor", e.target.value)
              }
            />
          </div>
          <div className="field">
            <label htmlFor="draw-phone">Contractor Phone / Cell</label>
            <PhoneInput
              value={drawDetails.phone}
              onChange={(value) => onChange("phone", value)}
              disabled
              greenDisable
            />
          </div>
        </div>

        <div className="fieldRow cols21">
          <div className="field">
            <label htmlFor="draw-email">Contractor Email</label>
            <Input
              id="draw-email"
              type="email"
              className=""
              value={drawDetails.email}
              disabled
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChange("email", e.target.value)
              }
            />
          </div>
          <div className="field">
            <label htmlFor="draw-period">
              Period To <span className="req">*</span>
            </label>
            <DateSelect
              id="draw-period"
              value={drawDetails.periodTo}
              max={drawDetails.date || getLocalToday()}
              error={!!errors.periodTo}
              onChange={(val) => {
                onChange("periodTo", val);
                if (val)
                  setErrors((prev) => ({ ...prev, periodTo: undefined }));
              }}
            />
            <ErrorMessage touched={!!errors.periodTo} error={errors.periodTo} />
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Payment Instructions">
        <div className="fieldRow cols2">
          <div className="field">
            <label htmlFor="draw-payee">
              Make check payable to <span className="req">*</span>
            </label>
            <Input
              id="draw-payee"
              className={errors.payee ? "error-input-red" : ""}
              value={drawDetails.payee}
              placeholder="Payee name"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                onChange("payee", e.target.value);
                if (e.target.value)
                  setErrors((prev) => ({ ...prev, payee: undefined }));
              }}
            />
            <ErrorMessage touched={!!errors.payee} error={errors.payee} />
          </div>
          <div className="field">
            <label htmlFor="draw-mailto">
              Mail check to <span className="req">*</span>
            </label>
            <Input
              id="draw-mailto"
              type="email"
              className={errors.mailTo ? "error-input-red" : ""}
              value={drawDetails.mailTo}
              placeholder="email@example.com"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const val = e.target.value;
                onChange("mailTo", val);
                if (!val) {
                  setErrors((prev) => ({
                    ...prev,
                    mailTo: "Mail check to is required",
                  }));
                } else if (!EMAIL_REGEX.test(val)) {
                  setErrors((prev) => ({
                    ...prev,
                    mailTo: "Enter a valid email address",
                  }));
                } else {
                  setErrors((prev) => ({ ...prev, mailTo: undefined }));
                }
              }}
            />
            <ErrorMessage touched={!!errors.mailTo} error={errors.mailTo} />
          </div>
        </div>
      </SectionCard>

      <div className="btnRow spaced">
        <Button className="btn btnSecondary" onClick={onBack}>
          <PrevIcon /> Back
        </Button>
        <div />
        <Button className="btn btnPrimary" onClick={handleNext}>
          Next: Line Items <NextIcon />
        </Button>
      </div>
    </section>
  );
};

export default DrawDetailsStep;
